type Field = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

export type EarlyInput = { field: Field; value: string; tag: string; type: string; name: string; id: string }

type EarlyInputWindow = Window & typeof globalThis & { __earlyInputs?: EarlyInput[] }

/**
 * Called once React has hydrated the server-rendered page: every field the person changed before then
 * (earlyInputs.inline.js) gets its typed value back, set so React's onChange sees it and its state follows — a value
 * typed on a slow phone is never lost (L.7). Only into the same field: if React replaced the element, the one with the
 * same id, or the only one with the same name, and always the same kind of field, so a password never lands anywhere
 * but a password field.
 */
export function restoreEarlyInputs(win: EarlyInputWindow = window) {
  const typed = win.__earlyInputs ?? []
  win.__earlyInputs = []
  for (const entry of typed) {
    const field = entry.field.isConnected ? entry.field : replacement(win.document, entry)
    if (!field || field.disabled || ('readOnly' in field && field.readOnly)) continue
    giveBack(win, field, entry.value)
  }
}

function sameKind(candidate: Element | null, entry: EarlyInput): candidate is Field {
  return !!candidate && candidate.tagName === entry.tag && (candidate as Field).type === entry.type
}

function replacement(doc: Document, entry: EarlyInput): Field | null {
  const byId = entry.id ? doc.getElementById(entry.id) : null
  if (sameKind(byId, entry)) return byId
  if (!entry.name) return null
  const named = [...doc.getElementsByName(entry.name)].filter((candidate) => sameKind(candidate, entry))
  return named.length === 1 ? (named[0] as Field) : null
}

// React keeps the value it last knew on the element and only fires onChange when the element's value differs from it.
// Setting the element's own value first moves what React knows away; the prototype's setter then puts the typed value
// in without React noticing, so the input event that follows reads as a change.
function giveBack(win: EarlyInputWindow, field: Field, value: string) {
  const focused = win.document.activeElement === field
  const selection = focused ? selectionOf(field) : null
  if (field instanceof win.HTMLSelectElement) {
    field.value = value
    field.dispatchEvent(new win.Event('change', { bubbles: true }))
    return
  }
  const proto = field instanceof win.HTMLTextAreaElement ? win.HTMLTextAreaElement.prototype : win.HTMLInputElement.prototype
  const setValue = Object.getOwnPropertyDescriptor(proto, 'value')?.set
  field.value = `${value}\u0000`
  if (setValue) setValue.call(field, value)
  else field.value = value
  field.dispatchEvent(new win.Event('input', { bubbles: true }))
  if (selection) restoreSelection(field, selection)
}

function selectionOf(field: Field): [number, number] | null {
  try {
    const { selectionStart, selectionEnd } = field as HTMLInputElement
    return selectionStart === null || selectionEnd === null ? null : [selectionStart, selectionEnd]
  } catch {
    return null
  }
}

function restoreSelection(field: Field, [start, end]: [number, number]) {
  try {
    ;(field as HTMLInputElement).setSelectionRange(start, end)
  } catch {
    // Fields like type=email or number have no selection to put back.
  }
}
